import type { ScheduleConfig } from "../components/organisms/DeliverStory/types";
import { isoDateTodayLocal, validateBaseSchedule } from "./scheduleTypes";

export type ScheduleField =
  | "every"
  | "repeatOn"
  | "refreshHour"
  | "timezone"
  | "startsOn"
  | "endsOn"
  | "endsAfter";

export type ScheduleErrors = Partial<Record<ScheduleField, string>>;

/**
 * DeliverStory schedule validator: shared base checks (every, repeatOn,
 * refreshHour, timezone) plus its start and active end-condition checks.
 *
 * DeliverStory only offers "Never"/"On" — its `ScheduleEndsField` renders
 * with `showAfter={false}` — so unlike Story refresh's validator there is
 * no `endsAfter` branch here.
 */
export const validateSchedule = (schedule: ScheduleConfig): ScheduleErrors => {
  const errors: ScheduleErrors = { ...validateBaseSchedule(schedule) };

  if (!schedule.startsOn) {
    errors.startsOn = "Pick a start date";
  } else if (schedule.startsOn < isoDateTodayLocal()) {
    errors.startsOn = "Start date cannot be in the past";
  }

  if (schedule.endsMode === "on") {
    if (!schedule.endsOn) {
      errors.endsOn = "Pick an end date";
    } else if (schedule.startsOn && schedule.endsOn < schedule.startsOn) {
      // Mirrors the backend's own DeliverySchedule cross-field check
      // (ends.date must be on/after starts_on) — catching it here means a
      // stale prefilled `startsOn` (e.g. from an older schedule loaded for
      // this story) surfaces as an inline field error instead of a failed
      // save.
      errors.endsOn = "End date can't be before the start date";
    }
  }

  return errors;
};

export const isScheduleValid = (schedule: ScheduleConfig): boolean =>
  Object.keys(validateSchedule(schedule)).length === 0;
